/**
 * businessBackup.service — Construye respaldos locales cifrados del negocio.
 * Depende de la capa transaccional y JSZip; exporta un paquete portable, sin UI ni credenciales de sesión.
 */
import {
  runTransaction,
  txRead,
  txRemove,
  txWriteRaw,
} from "@/lib/persistence/transactionalStore";
import { getBusinessStorageSnapshot } from "@/lib/persistence/idbMirrorBackend";
import { parsePersistedValue } from "@/mocks/helpers/storage";
import type { ReportExportFile } from "./reportExport.service";

interface BackupManifest {
  format: "altenot-backup";
  version: 1;
  createdAt: string;
  cipher: "AES-GCM";
  keyDerivation: "PBKDF2-SHA-256";
  iterations: number;
  datasets: Array<{ id: string; label: string; records: number }>;
}

interface BackupArchiveManifest extends BackupManifest {
  salt: number[];
  iv: number[];
}

interface BackupPayload {
  manifest: BackupManifest;
  contents: Record<string, string>;
}

export interface BusinessBackupResult {
  file: ReportExportFile;
  datasetCount: number;
  recordCount: number;
}

export interface BusinessBackupEstimate {
  datasetCount: number;
  recordCount: number;
  estimatedBytes: number;
}

export interface BusinessBackupRestoreResult extends BusinessBackupEstimate {
  createdAt: string;
  removedKeys: number;
}

const PBKDF2_ITERATIONS = 250_000;
export const BUSINESS_BACKUP_MAX_BYTES = 50 * 1024 * 1024;

function normalizeSecret(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9]/g, "");
}

function countRecords(serializedValue: string): number {
  try {
    const parsed: unknown = parsePersistedValue(serializedValue);
    return Array.isArray(parsed) ? parsed.length : parsed === null ? 0 : 1;
  } catch {
    return 1;
  }
}

function getDatasetLabel(key: string): string {
  if (key.startsWith("caja_") || key.startsWith("creditos_")) return "Caja";
  if (key.startsWith("inventory_")) return "Inventario";
  if (key.startsWith("personal_") || key.startsWith("payroll_"))
    return "Personal y nómina";
  if (key.startsWith("tienda_")) return "Tienda";
  if (key.startsWith("fidelizacion_")) return "Fidelización";
  if (key.startsWith("higiene_")) return "Higiene";
  if (key.startsWith("auditoria_")) return "Auditoría";
  return "Configuración del negocio";
}

function backupFilename(): string {
  return `altenot-respaldo-cifrado-${new Date().toISOString().slice(0, 10)}.zip`;
}

async function deriveKey(secret: string, salt: Uint8Array): Promise<CryptoKey> {
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    "PBKDF2",
    false,
    ["deriveKey"],
  );
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", hash: "SHA-256", salt, iterations: PBKDF2_ITERATIONS },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"],
  );
}

function buildEstimate(
  contents: Record<string, string>,
): BusinessBackupEstimate {
  const entries = Object.values(contents);
  return {
    datasetCount: entries.length,
    recordCount: entries.reduce(
      (total, value) => total + countRecords(value),
      0,
    ),
    estimatedBytes: entries.reduce(
      (total, value) => total + new TextEncoder().encode(value).byteLength,
      0,
    ),
  };
}

function validateByteArray(value: unknown, label: string): Uint8Array {
  if (
    !Array.isArray(value) ||
    value.some((item) => !Number.isInteger(item) || item < 0 || item > 255)
  ) {
    throw new Error(`El respaldo contiene un ${label} inválido.`);
  }
  return new Uint8Array(value);
}

function validateManifest(value: unknown): BackupArchiveManifest {
  if (!value || typeof value !== "object")
    throw new Error("El manifiesto del respaldo es inválido.");
  const manifest = value as Partial<BackupArchiveManifest>;
  if (
    manifest.format !== "altenot-backup" ||
    manifest.version !== 1 ||
    manifest.cipher !== "AES-GCM" ||
    manifest.keyDerivation !== "PBKDF2-SHA-256" ||
    manifest.iterations !== PBKDF2_ITERATIONS ||
    !Array.isArray(manifest.datasets)
  ) {
    throw new Error(
      "El respaldo no es compatible con esta versión de Altenot.",
    );
  }
  validateByteArray(manifest.salt, "salt");
  validateByteArray(manifest.iv, "vector de inicialización");
  return manifest as BackupArchiveManifest;
}

function validatePayload(
  value: unknown,
  archiveManifest: BackupArchiveManifest,
): BackupPayload {
  if (!value || typeof value !== "object")
    throw new Error("El contenido cifrado es inválido.");
  const payload = value as Partial<BackupPayload>;
  if (
    !payload.manifest ||
    payload.manifest.format !== archiveManifest.format ||
    payload.manifest.version !== archiveManifest.version ||
    payload.manifest.createdAt !== archiveManifest.createdAt ||
    !payload.contents ||
    typeof payload.contents !== "object" ||
    Array.isArray(payload.contents) ||
    Object.values(payload.contents).some((item) => typeof item !== "string")
  ) {
    throw new Error(
      "El contenido del respaldo no pasó la validación de integridad.",
    );
  }
  return payload as BackupPayload;
}

async function readEncryptedBackup(
  file: Blob,
  backupSecret: string,
): Promise<BackupPayload> {
  if (!globalThis.crypto?.subtle)
    throw new Error("Este dispositivo no permite abrir respaldos cifrados.");
  if (!backupSecret.trim())
    throw new Error("Ingresa el NIT o cédula del respaldo.");
  if (file.size === 0 || file.size > BUSINESS_BACKUP_MAX_BYTES) {
    throw new Error("El respaldo supera el límite de 50 MB o está vacío.");
  }
  const { default: JSZip } = await import("jszip");
  const archive = await JSZip.loadAsync(file);
  const manifestFile = archive.file("manifest.json");
  const encryptedFile = archive.file("backup.enc");
  if (!manifestFile || !encryptedFile)
    throw new Error("El archivo no contiene un respaldo Altenot válido.");
  let archiveManifest: BackupArchiveManifest;
  try {
    archiveManifest = validateManifest(
      JSON.parse(await manifestFile.async("text")),
    );
  } catch (error) {
    if (error instanceof Error) throw error;
    throw new Error("No se pudo leer el manifiesto del respaldo.");
  }
  try {
    const key = await deriveKey(
      backupSecret,
      validateByteArray(archiveManifest.salt, "salt"),
    );
    const decrypted = await crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: validateByteArray(archiveManifest.iv, "vector de inicialización"),
      },
      key,
      await encryptedFile.async("arraybuffer"),
    );
    return validatePayload(
      JSON.parse(new TextDecoder().decode(decrypted)),
      archiveManifest,
    );
  } catch {
    throw new Error(
      "No se pudo abrir el respaldo. Verifica el NIT o cédula y el archivo.",
    );
  }
}

export async function getBusinessBackupEstimate(): Promise<BusinessBackupEstimate> {
  return buildEstimate(await getBusinessStorageSnapshot());
}

export async function createEncryptedBusinessBackup(
  backupSecret: string,
): Promise<BusinessBackupResult> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("Este dispositivo no permite crear respaldos cifrados.");
  }

  const businessConfig = txRead<{ nit?: string }>("businessConfig", {});
  const configuredNit = normalizeSecret(businessConfig.nit ?? "");
  if (!configuredNit) {
    throw new Error(
      "Configura el NIT o cédula del negocio antes de generar un respaldo.",
    );
  }
  if (normalizeSecret(backupSecret) !== configuredNit) {
    throw new Error(
      "El NIT o cédula no coincide con la configuración del negocio.",
    );
  }

  const contents = await getBusinessStorageSnapshot();
  const estimate = buildEstimate(contents);
  const datasets = Object.entries(contents).map(([key, serializedValue]) => ({
    id: key,
    label: getDatasetLabel(key),
    records: countRecords(serializedValue),
  }));
  const manifest: BackupManifest = {
    format: "altenot-backup",
    version: 1,
    createdAt: new Date().toISOString(),
    cipher: "AES-GCM",
    keyDerivation: "PBKDF2-SHA-256",
    iterations: PBKDF2_ITERATIONS,
    datasets,
  };
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(backupSecret, salt);
  const encrypted = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    new TextEncoder().encode(JSON.stringify({ manifest, contents })),
  );
  const { default: JSZip } = await import("jszip");
  const archive = new JSZip();
  archive.file(
    "manifest.json",
    JSON.stringify(
      {
        ...manifest,
        salt: Array.from(salt),
        iv: Array.from(iv),
      },
      null,
      2,
    ),
  );
  archive.file("backup.enc", encrypted);
  const blob = await archive.generateAsync({
    type: "blob",
    compression: "DEFLATE",
  });
  if (blob.size > BUSINESS_BACKUP_MAX_BYTES) {
    throw new Error(
      "El respaldo supera el límite de 50 MB. Acota o archiva el histórico antes de continuar.",
    );
  }
  return {
    file: { blob, filename: backupFilename(), mimeType: "application/zip" },
    datasetCount: estimate.datasetCount,
    recordCount: estimate.recordCount,
  };
}

export async function previewEncryptedBusinessBackup(
  file: Blob,
  backupSecret: string,
): Promise<BusinessBackupEstimate & { createdAt: string }> {
  const payload = await readEncryptedBackup(file, backupSecret);
  return {
    ...buildEstimate(payload.contents),
    createdAt: payload.manifest.createdAt,
  };
}

export async function restoreEncryptedBusinessBackup(
  file: Blob,
  backupSecret: string,
): Promise<BusinessBackupRestoreResult> {
  const payload = await readEncryptedBackup(file, backupSecret);
  const currentContents = await getBusinessStorageSnapshot();
  const removedKeys = Object.keys(currentContents).filter(
    (key) => !(key in payload.contents),
  );
  runTransaction(() => {
    removedKeys.forEach((key) => txRemove(key));
    Object.entries(payload.contents).forEach(([key, value]) =>
      txWriteRaw(key, value),
    );
  });
  return {
    ...buildEstimate(payload.contents),
    createdAt: payload.manifest.createdAt,
    removedKeys: removedKeys.length,
  };
}
